import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { PRODUCTS, getProduct } from './analyticsData';
import { getCqaColumns, getCqaGroups } from './cqaSummaryData';

// ─────────────────────────────────────────────────────────────────────────────
// Project Analytics — In-Process Parameter Trend + Product Release Map.
// Standalone/demo screen: consumes the precomputed, PRODUCT-KEYED analytics
// derived from the Manufacturing Collaboration workbooks (see analyticsData.js).
//   • 2440  — CD19 CAR-LVV  : batches 2388-OXB-A-1 / 2785-OXB-B-1
//   • 575432 — Trastuzumab  : batch  TZB2401-DS
// The Product filter switches the whole dataset (batches, trends, release).
// ─────────────────────────────────────────────────────────────────────────────

const SERIES_COLORS = ['#2f7fd1', '#16a085'];
const FONT = '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, sans-serif';
const MONO = '"IBM Plex Mono", ui-monospace, monospace';

const DISABLED_TYPES = ['Batch Yield', 'CPPs', 'Consumption', 'Deviations'];

// ── numeric formatting helpers (ported from the design prototype) ─────────────
const fmt = (v, d) => {
  const n = Number(v);
  if (isNaN(n)) return String(v);
  if (Math.abs(n) >= 100000) return n.toExponential(2);
  return n.toFixed(d);
};
const decFor = (vals) => {
  const m = Math.max(0, ...vals.map((x) => Math.abs(x)));
  return m >= 100 ? 0 : m >= 10 ? 1 : m >= 1 ? 2 : 3;
};

// ── flag chip styles ──────────────────────────────────────────────────────────
const chip = (bg, color) => ({
  display: 'inline-block', fontSize: 11.5, fontWeight: 600,
  padding: '3px 10px', borderRadius: 12, background: bg, color,
});
const FLAG_WITHIN = chip('#e9f4df', '#3f6b1c');
const FLAG_NEAR = chip('#fbecd0', '#8a5a12');
const FLAG_OOS = chip('#fbe3e1', '#b23b30');
const BADGE_GREEN = chip('#e9f4df', '#3f6b1c');

// ── SVG line chart (ported from prototype lineChart) ──────────────────────────
function TrendChart({ series, batches }) {
  const t = series;
  if (!t) return <div style={{ color: '#8b9099', fontSize: 13 }}>No numeric data for this test.</div>;
  const shown = batches.filter((b) => t.byBatch[b] && t.byBatch[b].pts.length);
  const allVals = [];
  shown.forEach((b) => t.byBatch[b].pts.forEach((p) => allVals.push(p.val)));
  if (!allVals.length) return <div style={{ color: '#8b9099', fontSize: 13 }}>No numeric data.</div>;

  const W = 1040, H = 430, L = 88, R = 34, T = 42, B = 52;
  const iW = W - L - R, iH = H - T - B;
  const lo = Math.min(...allVals), hi = Math.max(...allVals);
  const span = hi - lo || Math.abs(hi) || 1;
  const ymin = lo - span * 0.18, ymax = hi + span * 0.18;
  const range = ymax - ymin || 1;
  const dec = decFor(allVals);
  const maxN = Math.max(...shown.map((b) => t.byBatch[b].pts.length));
  const px = (i) => L + 20 + (iW - 40) * (maxN <= 1 ? 0.5 : i / (maxN - 1));
  const py = (v) => T + iH - ((v - ymin) / range) * iH;

  const els = [];
  for (let k = 0; k <= 5; k++) {
    const tv = ymin + (range * k) / 5;
    const y = py(tv);
    els.push(<line key={`g${k}`} x1={L} y1={y} x2={L + iW} y2={y} stroke="#eceff3" />);
    els.push(<text key={`yl${k}`} x={L - 12} y={y + 4} textAnchor="end" fontSize={12.5} fill="#8b9099">{fmt(tv, dec)}</text>);
  }
  els.push(<line key="ax" x1={L} y1={T} x2={L} y2={T + iH} stroke="#c7ccd4" />);
  els.push(<line key="axb" x1={L} y1={T + iH} x2={L + iW} y2={T + iH} stroke="#c7ccd4" />);
  for (let i = 0; i < maxN; i++) {
    els.push(<text key={`xl${i}`} x={px(i)} y={T + iH + 24} textAnchor="middle" fontSize={12} fill="#4a4f59">{i + 1}</text>);
  }
  shown.forEach((b, bi) => {
    const col = SERIES_COLORS[bi % SERIES_COLORS.length];
    const pts = t.byBatch[b].pts;
    const mean = t.byBatch[b].mean;
    if (shown.length === 1) {
      els.push(<line key="mn" x1={L} y1={py(mean)} x2={L + iW} y2={py(mean)} stroke="#9aa0a8" strokeWidth={1.5} strokeDasharray="7 5" />);
      els.push(<text key="mnl" x={L + iW - 2} y={py(mean) - 7} textAnchor="end" fontSize={12} fill="#9aa0a8">{`mean ${fmt(mean, dec)}`}</text>);
    }
    let d = '';
    pts.forEach((p, i) => { d += `${i ? 'L' : 'M'}${px(i)} ${py(p.val)} `; });
    els.push(<path key={`ln${bi}`} d={d} fill="none" stroke={col} strokeWidth={2.4} strokeLinejoin="round" />);
    pts.forEach((p, i) => {
      const c = p.oos ? '#d94436' : p.near ? '#e0a32f' : '#6aa53d';
      els.push(<circle key={`m${bi}_${i}`} cx={px(i)} cy={py(p.val)} r={5} fill={c} stroke="#fff" strokeWidth={1.5} />);
    });
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', fontFamily: FONT }}>{els}</svg>
  );
}

// ── small reusable bits ────────────────────────────────────────────────────────
const Chevron = ({ color = '#7b818b', size = 13, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.2} style={style}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);
const BackArrow = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
    <path d="M15 6l-6 6 6 6" />
  </svg>
);
const selectWrap = { position: 'relative' };
const nativeSelectSty = {
  width: '100%', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
  fontFamily: 'inherit', fontSize: 14, fontWeight: 500, color: '#1f2430', background: '#fff',
  border: '1.5px solid #d7dbe2', borderRadius: 9, padding: '11px 36px 11px 13px', cursor: 'pointer',
};
const chevAbs = { position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' };
const fieldLabel = { display: 'block', fontSize: 12.5, fontWeight: 600, color: '#4a4f59', marginBottom: 7 };

// ── Type dropdown (In-Process Tests / Product Release Tests + disabled) ────────
function TypeDropdown({ selType, onSelect, compact }) {
  const [open, setOpen] = useState(false);
  const pad = compact ? '9px 12px' : '11px 13px';
  const fs = compact ? 13.5 : 14;
  const optRow = (active, onClick) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 11px', borderRadius: 7,
    cursor: 'pointer', background: active ? 'rgba(47,111,209,.08)' : 'transparent',
  });
  const box = (active) => (active
    ? { width: 17, height: 17, borderRadius: 4, background: '#2f6fd1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }
    : { width: 17, height: 17, borderRadius: 4, border: '1.5px solid #c2c7d0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' });
  const check = (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 12l5 5 9-10" /></svg>
  );
  const pick = (val) => { onSelect(val); setOpen(false); };
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontFamily: 'inherit', fontSize: fs, fontWeight: 600, color: '#1f2430', background: '#fff',
          border: '1.5px solid #2f6fd1', borderRadius: 9, padding: pad, cursor: 'pointer',
          boxShadow: '0 0 0 3px rgba(47,111,209,.12)',
        }}
      >
        <span style={{ whiteSpace: 'nowrap' }}>{selType}</span>
        <Chevron color="#2f6fd1" size={compact ? 12 : 13} style={{ flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 6px)', background: '#fff', border: '1px solid #e0e3ea', borderRadius: 10, boxShadow: '0 14px 36px rgba(20,24,40,.16)', padding: 6, zIndex: 30 }}>
          <div onClick={() => pick('In-Process Tests')} style={optRow(selType === 'In-Process Tests')}>
            <span style={box(selType === 'In-Process Tests')}>{selType === 'In-Process Tests' && check}</span>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1f2430' }}>In-Process Tests</span>
          </div>
          <div onClick={() => pick('Product Release Tests')} style={optRow(selType === 'Product Release Tests')}>
            <span style={box(selType === 'Product Release Tests')}>{selType === 'Product Release Tests' && check}</span>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1f2430' }}>Product Release Tests</span>
          </div>
          {DISABLED_TYPES.map((name) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 11px', borderRadius: 7, cursor: 'not-allowed', opacity: 0.5 }}>
              <span style={{ width: 17, height: 17, borderRadius: 4, border: '1.5px solid #c2c7d0', flexShrink: 0 }} />
              <span style={{ fontSize: 13.5, fontWeight: 500, color: '#6b7280', flex: 1 }}>{name}</span>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#aab0ba" strokeWidth="1.8"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── stat tile ──────────────────────────────────────────────────────────────────
const StatTile = ({ label, value, warn }) => (
  <div style={{ flex: 1, minWidth: 120, background: warn ? '#fdf6ec' : '#f7f8fa', border: `1px solid ${warn ? '#f3e3c4' : '#edeff3'}`, borderRadius: 10, padding: '12px 14px' }}>
    <div style={{ fontSize: 11, color: warn ? '#a9802f' : '#8b9099', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
    <div style={{ fontSize: 19, fontWeight: 700, fontFamily: MONO, marginTop: 3, color: warn ? '#a9802f' : 'inherit' }}>{value}</div>
  </div>
);

// table cell helpers
const th = (align = 'left') => ({ display: 'table-cell', textAlign: align, padding: '10px 14px', fontWeight: 600, color: '#5b616b', borderBottom: '1px solid #e6e8ee', position: 'sticky', top: 0, zIndex: 1, background: '#f7f8fa' });
const td = (extra = {}) => ({ display: 'table-cell', padding: '9px 14px', borderBottom: '1px solid #f0f1f4', ...extra });

// ─────────────────────────────────────────────────────────────────────────────
const ProjectAnalytics = () => {
  const history = useHistory();
  const [screen, setScreen] = useState('filters'); // 'filters' | 'dashboard'
  const [selProductId, setSelProductId] = useState(PRODUCTS[0].id);
  const [selType, setSelType] = useState('In-Process Tests');
  const [selBatch, setSelBatch] = useState('All batches');
  const [selectedTest, setSelectedTest] = useState(null);
  const [openRows, setOpenRows] = useState({});

  // Active product drives the whole dataset (batches / trends / release / meta).
  const product = getProduct(selProductId);
  const analytics = product.analytics;
  const meta = product;

  const isRelease = selType === 'Product Release Tests';
  const batchOptions = ['All batches', ...analytics.batches];
  const shownBatches = selBatch === 'All batches' ? analytics.batches.slice() : [selBatch];
  const scopeLabel = selBatch === 'All batches'
    ? (analytics.batches.length > 1 ? `All batches (${analytics.batches.join(', ')})` : analytics.batches[0])
    : selBatch;

  // Switching product resets batch + trend selection to that product's defaults.
  const onSelectProduct = (id) => {
    setSelProductId(id);
    setSelBatch('All batches');
    setSelectedTest(null);
  };

  // Trend dropdown: product.allowedTests limits it; null → every numeric series.
  const testOptions = product.allowedTests
    ? (analytics.plot1Order || []).filter((o) => product.allowedTests.includes(o))
    : (analytics.plot1Order || []).slice();
  const test = selectedTest && testOptions.indexOf(selectedTest) >= 0 ? selectedTest : testOptions[0] || '';
  const t = analytics.plot1[test];

  // ── Plot-1 rows + stats ──
  const { p1Rows, statMean, statRange, statN, statNear, statOos } = useMemo(() => {
    const rows = [];
    const allVals = [];
    let near = 0;
    let oos = 0;
    if (t) {
      shownBatches.forEach((b) => {
        const bb = t.byBatch[b];
        if (!bb) return;
        const bdec = decFor(bb.pts.map((p) => p.val));
        bb.pts.forEach((p) => {
          allVals.push(p.val);
          if (p.oos) oos += 1;
          else if (p.near) near += 1;
          const delta = p.val - bb.mean;
          rows.push({
            batch: b,
            seq: p.seq,
            result: fmt(p.val, bdec),
            mean: fmt(bb.mean, bdec),
            delta: (delta >= 0 ? '+' : '') + fmt(delta, bdec),
            flag: p.oos ? 'Out of spec' : p.near ? 'Near / outlier' : 'Within',
            flagStyle: p.oos ? FLAG_OOS : p.near ? FLAG_NEAR : FLAG_WITHIN,
          });
        });
      });
    }
    const dec = decFor(allVals);
    const lo = allVals.length ? Math.min(...allVals) : 0;
    const hi = allVals.length ? Math.max(...allVals) : 0;
    const mean = allVals.length ? allVals.reduce((a, c) => a + c, 0) / allVals.length : 0;
    return {
      p1Rows: rows,
      statMean: fmt(mean, dec),
      statRange: `${fmt(lo, dec)} – ${fmt(hi, dec)}`,
      statN: String(allVals.length),
      statNear: String(near),
      statOos: String(oos),
    };
  }, [t, test, selBatch, selProductId]); // eslint-disable-line react-hooks/exhaustive-deps

  const plot1Subtitle = `${t ? `${t.spec && /report/i.test(t.spec) ? 'Report-result parameter' : `Spec: ${t.spec}`} · ${t.uom || ''}` : ''} · ${scopeLabel}`;

  // ── Release data ──
  const relBatches = analytics.batches.filter((b) => selBatch === 'All batches' || selBatch === b);
  const releaseRows = relBatches.map((b) => {
    const recs = analytics.release[b] || [];
    const open = openRows[b] !== false;
    return {
      batch: b,
      count: recs.length,
      open,
      cards: recs.map((r) => ({
        name: r.spec || r.method,
        result: r.method + (r.result && r.result !== 'NA' ? ` · ${r.result}${r.uom && r.uom !== 'NA' ? ` ${r.uom}` : ''}` : ''),
        status: r.status || 'Approved',
      })),
    };
  });
  const releaseTableRows = [];
  relBatches.forEach((b) => (analytics.release[b] || []).forEach((r) => releaseTableRows.push({
    batch: b, method: r.method, spec: r.spec, result: r.result, uom: r.uom, status: r.status || 'Approved',
  })));

  const toggleRow = (b) => setOpenRows((prev) => ({ ...prev, [b]: !(prev[b] !== false) }));

  // ── Filter strip (shared markup for both screens) ──
  const renderFilters = (compact) => {
    const lbl = compact
      ? { display: 'block', fontSize: 11.5, fontWeight: 600, color: '#4a4f59', marginBottom: 5 }
      : fieldLabel;
    const sel = compact ? { ...nativeSelectSty, fontSize: 13.5, padding: '9px 32px 9px 12px' } : nativeSelectSty;
    return (
      <>
        <div style={{ flex: 1, minWidth: compact ? 160 : 190 }}>
          <label style={lbl}>Product</label>
          <div style={selectWrap}>
            <select style={sel} value={selProductId} onChange={(e) => onSelectProduct(e.target.value)}>
              {PRODUCTS.map((p) => <option key={p.id} value={p.id}>{p.product}</option>)}
            </select>
            <Chevron style={chevAbs} size={compact ? 12 : 13} />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: compact ? 150 : 170 }}>
          <label style={lbl}>Batch Number</label>
          <div style={selectWrap}>
            <select style={sel} value={selBatch} onChange={(e) => setSelBatch(e.target.value)}>
              {batchOptions.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <Chevron style={chevAbs} size={compact ? 12 : 13} />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: compact ? 180 : 210, position: 'relative' }}>
          <label style={lbl}>Type</label>
          <TypeDropdown selType={selType} onSelect={setSelType} compact={compact} />
        </div>
      </>
    );
  };

  // ── FILTERS SCREEN ──
  if (screen === 'filters') {
    return (
      <div style={{ fontFamily: FONT, background: '#f4f5f7', minHeight: 'calc(100vh - 120px)', color: '#1f2430', padding: '26px 30px 48px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22 }}>
            <span onClick={() => history.goBack()} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, color: '#5b616b', cursor: 'pointer' }}>
              <BackArrow />Back
            </span>
            <h2 style={{ fontSize: 25, fontWeight: 700, margin: 0, letterSpacing: '-.015em' }}>Batch Analytics</h2>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e6e8ee', borderRadius: 14, padding: '26px 28px', boxShadow: '0 1px 2px rgba(20,24,40,.04)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#8b9099', marginBottom: 18 }}>Filters</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 18 }}>
              {renderFilters(false)}
              <button
                type="button"
                onClick={() => setScreen('dashboard')}
                style={{ border: 'none', cursor: 'pointer', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, padding: '12px 26px', borderRadius: 9, background: '#2f6fd1', boxShadow: '0 5px 14px rgba(47,111,209,.32)', whiteSpace: 'nowrap' }}
              >
                View Analytics
              </button>
            </div>
          </div>

          <div style={{ marginTop: 14, fontSize: 12.5, color: '#9499a3', display: 'flex', alignItems: 'center', gap: 7 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 8v5l3 2" /></svg>
            {`Last Synced @ ${meta.lastSynced} · Source ${meta.source} · ${analytics.totalResults} in-process result rows · ${analytics.batches.length} ${analytics.batches.length === 1 ? 'batch' : 'batches'}`}
          </div>
        </div>
      </div>
    );
  }

  // ── CQA & CPPs SUMMARY SCREEN (full page, sticky header) ──
  if (screen === 'cqa') {
    const cqaCols = getCqaColumns(shownBatches);
    const cqaGroups = getCqaGroups();
    // sticky header cell + body cell styles for the native <table>
    const hCell = (extra = {}) => ({
      position: 'sticky', top: 0, zIndex: 2, background: '#eef1f5', textAlign: 'left',
      padding: '12px 16px', fontSize: 12.5, fontWeight: 700, color: '#3a3f4a',
      borderBottom: '1px solid #d7dbe2', whiteSpace: 'nowrap', ...extra,
    });
    const bCell = (extra = {}) => ({ padding: '10px 16px', fontSize: 13, borderBottom: '1px solid #eef0f3', verticalAlign: 'top', ...extra });

    return (
      <div style={{ fontFamily: FONT, background: '#f4f5f7', minHeight: 'calc(100vh - 120px)', color: '#1f2430', padding: '26px 30px 48px' }}>
        <div style={{ maxWidth: 1320, margin: '0 auto' }}>
          <button type="button" onClick={() => setScreen('dashboard')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#5b616b', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 10 }}>
            <BackArrow />Back to analytics
          </button>
          <h2 style={{ fontSize: 25, fontWeight: 700, margin: 0, letterSpacing: '-.015em' }}>CQA &amp; CPPs Summary</h2>
          <div style={{ fontSize: 13, color: '#8b9099', marginTop: 4, marginBottom: 18 }}>{`${meta.product} · ${scopeLabel} · Vector Substance Operational Data`}</div>

          <section style={{ background: '#fff', border: '1px solid #e6e8ee', borderRadius: 14, boxShadow: '0 1px 2px rgba(20,24,40,.04)', overflow: 'hidden' }}>
            <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 250px)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
                <thead>
                  <tr>
                    <th style={hCell({ minWidth: 170 })}>Process Stage</th>
                    <th style={hCell({ minWidth: 260 })}>Attribute</th>
                    {cqaCols.map((c) => (
                      <th key={c.batch} style={hCell({ minWidth: 190 })}>
                        {c.batch}{c.gmp ? ` (${c.gmp})` : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cqaGroups.map((g, gi) => g.rows.map((r, ri) => (
                    <tr key={`${gi}-${ri}`} style={{ background: gi % 2 ? '#fafbfc' : '#fff' }}>
                      {ri === 0 && (
                        <td rowSpan={g.rows.length} style={bCell({ fontWeight: 700, color: '#1f2430', borderRight: '1px solid #eef0f3', background: gi % 2 ? '#f3f5f8' : '#f7f9fb' })}>
                          {g.stage}
                        </td>
                      )}
                      <td style={bCell({ color: '#5b616b' })}>{r.attribute}</td>
                      {cqaCols.map((c) => (
                        <td key={c.batch} style={bCell({ fontFamily: MONO, fontWeight: 600, color: '#1f2430', whiteSpace: 'pre-line' })}>
                          {r.values[c.srcKey] || '—'}
                        </td>
                      ))}
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    );
  }

  // ── DASHBOARD SCREEN ──
  return (
    <div style={{ fontFamily: FONT, background: '#f4f5f7', minHeight: 'calc(100vh - 120px)', color: '#1f2430', padding: '26px 30px 48px' }}>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 18 }}>
          <div>
            <button type="button" onClick={() => setScreen('filters')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#5b616b', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 8 }}>
              <BackArrow />Back to filters
            </button>
            <h2 style={{ fontSize: 25, fontWeight: 700, margin: 0, letterSpacing: '-.015em' }}>Batch Analytics</h2>
            <div style={{ fontSize: 13, color: '#8b9099', marginTop: 4 }}>{`${meta.productId} · ${meta.productShort} · ${meta.cmo} · ${scopeLabel}`}</div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12 }}>
            <div style={{ fontSize: 12, color: '#9499a3' }}>
              {`Last Synced @ ${meta.lastSynced} `}
              <span style={{ color: '#e8623f', fontWeight: 600, cursor: 'pointer' }}>Refresh ⟳</span>
            </div>
            <button
              type="button"
              onClick={() => setScreen('cqa')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: 'none', cursor: 'pointer', color: '#fff', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, padding: '11px 20px', borderRadius: 9, background: '#2b2f6b', boxShadow: '0 5px 14px rgba(43,47,107,.30)', whiteSpace: 'nowrap' }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /></svg>
              View CQA &amp; CPPs Summary
            </button>
          </div>
        </div>

        {/* editable filter strip */}
        <section style={{ background: '#fff', border: '1px solid #e6e8ee', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 2px rgba(20,24,40,.04)', marginBottom: 22 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#8b9099', marginRight: 4, alignSelf: 'center' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 5h18l-7 8v6l-4-2v-4z" /></svg>Filters
            </div>
            {renderFilters(true)}
          </div>
        </section>

        {!isRelease ? (
          // ── IN-PROCESS: Parameter Trend ──
          <section style={{ background: '#fff', border: '1px solid #e6e8ee', borderRadius: 14, boxShadow: '0 1px 2px rgba(20,24,40,.04)', overflow: 'hidden' }}>
            <div style={{ padding: '24px 26px 26px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ fontSize: 19, fontWeight: 700, margin: '0 0 4px' }}>In-Process Parameter Trend</h3>
                  <p style={{ fontSize: 13, color: '#8b9099', margin: 0 }}>{plot1Subtitle}</p>
                </div>
                <div style={{ minWidth: 300 }}>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: '#8b9099', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.05em' }}>Selected test — Unit Procedure</label>
                  <div style={selectWrap}>
                    <select
                      style={{ ...nativeSelectSty, fontSize: 13.5, fontWeight: 600, padding: '10px 34px 10px 12px' }}
                      value={test}
                      onChange={(e) => setSelectedTest(e.target.value)}
                    >
                      {testOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <Chevron style={chevAbs} />
                  </div>
                </div>
              </div>

              {/* legend */}
              <div style={{ display: 'flex', gap: 22, alignItems: 'center', margin: '18px 0 6px', fontSize: 12.5, color: '#5b616b', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 11, height: 11, borderRadius: '50%', background: '#6aa53d' }} />Within spec</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 11, height: 11, borderRadius: '50%', background: '#e0a32f' }} />Near limit / outlier</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 20, height: 0, borderTop: '2px dashed #9aa0a8' }} />Batch mean</span>
                {shownBatches.map((b, i) => (
                  <span key={b} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 20, height: 0, borderTop: `2.5px solid ${SERIES_COLORS[i % SERIES_COLORS.length]}` }} />{b}</span>
                ))}
              </div>

              <div style={{ margin: '6px 0 18px' }}>
                <TrendChart series={t} batches={shownBatches} />
              </div>

              {/* stat tiles */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                <StatTile label="Mean" value={statMean} />
                <StatTile label="Range" value={statRange} />
                <StatTile label="n Readings" value={statN} />
                <StatTile label="Near / outlier" value={statNear} warn />
                <StatTile label="Out of spec" value={statOos} />
              </div>

              {/* detail table */}
              <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)', border: '1px solid #edeff3', borderRadius: 10 }}>
                <div style={{ display: 'table', width: '100%', minWidth: 640, fontSize: 13, borderSpacing: 0 }}>
                  <div style={{ display: 'table-row', background: '#f7f8fa' }}>
                    <div style={th('left')}>Batch</div>
                    <div style={th('center')}>Seq</div>
                    <div style={th('right')}>Result</div>
                    <div style={th('right')}>Batch Mean</div>
                    <div style={th('right')}>Δ vs Mean</div>
                    <div style={th('left')}>Flag</div>
                  </div>
                  {p1Rows.map((r, i) => (
                    <div key={i} style={{ display: 'table-row' }}>
                      <div style={td({ fontWeight: 600, fontFamily: MONO })}>{r.batch}</div>
                      <div style={td({ textAlign: 'center', color: '#5b616b' })}>{r.seq}</div>
                      <div style={td({ textAlign: 'right', fontFamily: MONO, fontWeight: 600 })}>{r.result}</div>
                      <div style={td({ textAlign: 'right', fontFamily: MONO, color: '#5b616b' })}>{r.mean}</div>
                      <div style={td({ textAlign: 'right', fontFamily: MONO, color: '#5b616b' })}>{r.delta}</div>
                      <div style={td()}><span style={r.flagStyle}>{r.flag}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        ) : (
          // ── PRODUCT RELEASE: Release Map ──
          <section style={{ background: '#fff', border: '1px solid #e6e8ee', borderRadius: 14, boxShadow: '0 1px 2px rgba(20,24,40,.04)', padding: '22px 24px 24px', marginBottom: 22, overflow: 'hidden' }}>
            <div style={{ height: 4, background: 'linear-gradient(90deg,#4c2f96,#2f6fd1)', margin: '-22px -24px 18px' }} />
            <div>
              <h3 style={{ fontSize: 19, fontWeight: 700, margin: '0 0 4px' }}>{`Release Map — ${meta.productShort} Drug Substance`}</h3>
              <p style={{ fontSize: 13, color: '#8b9099', margin: 0, maxWidth: 900 }}>Batches as swimlanes; each release test shows its result with a colour-coded conformance band against its registered specification limit.</p>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', margin: '16px 0 8px', fontSize: 12.5, color: '#5b616b' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ width: 12, height: 12, borderRadius: 3, background: '#e6f2e2', border: '1px solid #d2e7ca', display: 'inline-block' }} />Comfortable margin</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ width: 12, height: 12, borderRadius: 3, background: '#e6eefb', border: '1px solid #d3e0f5', display: 'inline-block' }} />Marginal / near limit</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ width: 12, height: 12, borderRadius: 3, background: '#eef0f3', border: '1px solid #e3e7ec', display: 'inline-block' }} />Report result (no numeric limit)</span>
            </div>

            <div style={{ border: '1px solid #dfe3ea', borderRadius: 10, overflow: 'hidden', marginTop: 8 }}>
              {releaseRows.map((r) => (
                <div key={r.batch} style={{ borderTop: '1px solid #e6e8ee' }}>
                  <div onClick={() => toggleRow(r.batch)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: '#faf8fe', cursor: 'pointer' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.4" style={{ transition: 'transform .15s ease', transform: `rotate(${r.open ? 90 : 0}deg)` }}><path d="M9 6l6 6-6 6" /></svg>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a', letterSpacing: '-.01em' }}>{r.batch}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: '#1a1a1a', background: '#eef2fb', borderRadius: 6, padding: '3px 10px' }}>{`${r.count} tests`}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: '#3f6b1c' }}>All results approved</span>
                  </div>
                  {r.open && (
                    <div style={{ padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: 9 }}>
                      {r.cards.map((c, i) => (
                        <div key={i} style={{ width: 210, border: '1px solid #e7e9ef', borderRadius: 9, padding: '9px 11px', background: '#fff', boxShadow: '0 1px 2px rgba(20,24,40,.05)' }}>
                          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#1f2430', marginBottom: 6, lineHeight: 1.3 }}>{c.name}</div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                            <span style={{ fontFamily: MONO, fontSize: 12, color: '#5b616b' }}>{c.result}</span>
                            <span style={BADGE_GREEN}>{c.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <h4 style={{ fontSize: 14, fontWeight: 700, margin: '22px 0 10px' }}>Detailed Release Results</h4>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 320px)', border: '1px solid #edeff3', borderRadius: 10 }}>
              <div style={{ display: 'table', width: '100%', minWidth: 820, fontSize: 13, borderSpacing: 0 }}>
                <div style={{ display: 'table-row', background: '#f7f8fa' }}>
                  <div style={th('left')}>Batch</div>
                  <div style={th('left')}>Method ID</div>
                  <div style={th('left')}>Specification Limit</div>
                  <div style={th('right')}>Result</div>
                  <div style={th('left')}>UOM</div>
                  <div style={th('center')}>Status</div>
                </div>
                {releaseTableRows.map((r, i) => (
                  <div key={i} style={{ display: 'table-row' }}>
                    <div style={td({ fontWeight: 600, fontFamily: MONO, color: '#4c2f96' })}>{r.batch}</div>
                    <div style={td({ fontFamily: MONO })}>{r.method}</div>
                    <div style={td({ color: '#5b616b' })}>{r.spec}</div>
                    <div style={td({ textAlign: 'right', fontFamily: MONO, fontWeight: 600 })}>{r.result}</div>
                    <div style={td({ color: '#5b616b' })}>{r.uom}</div>
                    <div style={td({ textAlign: 'center' })}><span style={BADGE_GREEN}>{r.status}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProjectAnalytics;
